/**
 * KAN-670: блок "Продавцы" на странице Telegram-аккаунтов: справочник продавцов
 * (имя ссылкой на страницу покупки, рейтинг кликабельными звёздами, счётчики
 * аккаунтов, заметка), кнопки "Добавить", "Изменить", "Удалить" (с подтверждением;
 * 409 от сервера показывается текстом).
 */
import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { SellerDialog } from '@/components/telegram/SellerDialog';
import { SellerRatingEditor } from '@/components/telegram/SellerRatingEditor';
import { useTelegramSellerMutations, useTelegramSellers } from '@/hooks/useTelegramSellers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { safeHttpUrl } from '@/lib/sellerLink';
import type { TelegramSeller } from '@/types/telegramSeller';

export const SELLERS_BLOCK_HELP =
  'Продавцы, у которых покупаются аккаунты. Продавец выбирается при подключении и импорте аккаунта; удалить можно только продавца без аккаунтов.';
const EMPTY_TEXT = 'Продавцов нет. Нажмите "Добавить".';

type DialogState = { mode: 'create' } | { mode: 'edit'; seller: TelegramSeller } | null;

function SellerRow({
  seller,
  onEdit,
  onDelete,
}: {
  seller: TelegramSeller;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const page = safeHttpUrl(seller.url);

  return (
    <TableRow data-testid={`seller-row-${seller.id}`}>
      <TableCell className="text-sm">
        {page ? (
          <a
            href={page}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 hover:underline"
            data-testid={`seller-row-link-${seller.id}`}
          >
            {seller.name}
          </a>
        ) : (
          <span className="font-medium" data-testid={`seller-row-name-${seller.id}`}>
            {seller.name}
          </span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <SellerRatingEditor
            seller={seller}
            testId={`seller-row-rating-${seller.id}`}
            errorTestId={`seller-row-error-${seller.id}`}
          />
        </div>
      </TableCell>
      <TableCell className="text-sm text-gray-700" data-testid={`seller-row-counts-${seller.id}`}>
        {seller.accounts_total} / {seller.accounts_alive} / {seller.accounts_written_off}
      </TableCell>
      <TableCell className="max-w-[320px] whitespace-pre-wrap break-words text-xs text-gray-600">
        {seller.notes}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-testid={`seller-row-edit-${seller.id}`}
            onClick={onEdit}
          >
            <Pencil className="mr-1 h-3.5 w-3.5" />
            Изменить
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs text-red-700"
            data-testid={`seller-row-delete-${seller.id}`}
            onClick={onDelete}
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            Удалить
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function SellersBlock(): JSX.Element {
  const { data, isLoading, isError } = useTelegramSellers();
  const { remove } = useTelegramSellerMutations();
  const sellers = data ?? [];
  const [dialog, setDialog] = useState<DialogState>(null);
  const [toDelete, setToDelete] = useState<TelegramSeller | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const confirmDelete = (): void => {
    if (!toDelete) return;
    const seller = toDelete;
    setToDelete(null);
    setDeleteError(null);
    remove.mutate(seller.id, {
      onError: (err) =>
        setDeleteError(
          `${seller.name}: ${extractErrorMessage(err, 'Не удалось удалить продавца')}`
        ),
    });
  };

  return (
    <section data-testid="sellers-block" className="mt-6">
      <div className="mb-1 flex items-center gap-3">
        <h3 className="text-base font-semibold text-gray-900">Продавцы</h3>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          data-testid="seller-add"
          onClick={() => setDialog({ mode: 'create' })}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Добавить
        </Button>
      </div>
      <p className="mb-3 text-xs text-gray-500" data-testid="sellers-help">
        {SELLERS_BLOCK_HELP}
      </p>
      {deleteError && (
        <p className="mb-2 text-xs text-red-700" data-testid="seller-delete-error">
          {deleteError}
        </p>
      )}
      {isError && (
        <p className="text-sm text-red-700" data-testid="sellers-error">
          Не удалось загрузить продавцов
        </p>
      )}
      {!isLoading && !isError && sellers.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="sellers-empty">
          {EMPTY_TEXT}
        </p>
      )}
      {sellers.length > 0 && (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <Table aria-label="Продавцы аккаунтов">
            <TableHeader>
              <TableRow>
                <TableHead>Продавец</TableHead>
                <TableHead>Рейтинг</TableHead>
                <TableHead title="всего / живых / списанных">Аккаунтов (всего / живых / списанных)</TableHead>
                <TableHead>Заметка</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sellers.map((seller) => (
                <SellerRow
                  key={seller.id}
                  seller={seller}
                  onEdit={() => setDialog({ mode: 'edit', seller })}
                  onDelete={() => setToDelete(seller)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <SellerDialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        seller={dialog?.mode === 'edit' ? dialog.seller : null}
      />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        view={{
          title: 'Удалить продавца?',
          description: 'Карточка продавца будет удалена. Продавец с аккаунтами не удаляется.',
          confirm: 'Удалить',
        }}
        extra={toDelete ? `${toDelete.name}.` : null}
        onConfirm={confirmDelete}
        testId="seller-delete-confirm"
      />
    </section>
  );
}

export default SellersBlock;
